// Environment switcher — reactive state + switch orchestration for the topbar.
//
// Exposes the cached environment list (see services/environmentSwitcher), the
// currently-bound one, and `switchEnvironment(dn)`, which:
//   1. asks the backend to assume the target environment (password-less; the
//      response's Set-Cookie rebinds the session — nothing is stored in JS),
//   2. clears the account-scoped localStorage caches,
//   3. tells other tabs to reload (cross-tab guard — the auth cookie is shared
//      across tabs, so a switch in one tab would otherwise silently re-scope the
//      others), and
//   4. reloads the current page so the shell bootstrap rehydrates for the new env.
//
// The list is only populated for admins with >1 environment (single-environment
// admins have nothing to switch to). It is captured at login (see formLogin.vue).

import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import registration from '../services/registration';
import { endRumSession } from '../helpers/rum';
import {
  parseEnvironments,
  readEnvironments,
  readCurrentEnvironment,
  writeCurrentEnvironment,
  clearTenantCaches,
} from '../services/environmentSwitcher';

const CROSS_TAB_CHANNEL = 'flexigo-environment-switch';
const SWITCHED_MESSAGE = 'environment-switched';

export default function useEnvironmentSwitcher() {
  const environmentsRaw = ref(readEnvironments());
  const currentValue = ref(readCurrentEnvironment());
  const isSwitching = ref(false);
  const error = ref(null);

  const environments = computed(() => parseEnvironments(environmentsRaw.value));
  const currentEnvironment = computed(
    () => environments.value.find((env) => env.value === currentValue.value) || null,
  );
  // Only worth showing when there is more than one environment to move between.
  const showSwitcher = computed(() => environments.value.length > 1);

  let channel = null;
  const hasBroadcast = typeof BroadcastChannel !== 'undefined';

  async function switchEnvironment(dn) {
    if (!dn || dn === currentValue.value || isSwitching.value) return;
    isSwitching.value = true;
    error.value = null;
    try {
      const response = await registration.switchAccount(dn);
      // PIN-gated tenant: we can't complete a silent switch — surface an error
      // rather than reloading into an unverified session. (Contract TBD: the BE
      // may instead bypass/reject PIN for assume — revisit when Part A lands.)
      if (response?.pinRequired) {
        error.value = new Error('environment-switch-pin-required');
        isSwitching.value = false;
        return;
      }
      // Cookie-based session: the backend Set-Cookie already rebound us. Persist
      // the new current env, drop stale tenant caches, tell other tabs, reload.
      writeCurrentEnvironment(dn);
      clearTenantCaches();
      if (channel) channel.postMessage({ type: SWITCHED_MESSAGE });
      // each tenant gets its own Datadog RUM session
      endRumSession();
      window.location.reload();
    } catch (e) {
      console.error('[environment-switcher] switch failed', e);
      error.value = e;
      isSwitching.value = false;
    }
  }

  onMounted(() => {
    if (!hasBroadcast) return;
    channel = new BroadcastChannel(CROSS_TAB_CHANNEL);
    channel.onmessage = (event) => {
      // Another tab switched environment; the shared auth cookie now points
      // elsewhere, so reload to rebind this tab (or it would show stale/foreign data).
      if (event?.data?.type === SWITCHED_MESSAGE) {
        endRumSession();
        window.location.reload();
      }
    };
  });

  onBeforeUnmount(() => {
    if (channel) channel.close();
  });

  return {
    environments,
    currentEnvironment,
    showSwitcher,
    isSwitching,
    error,
    switchEnvironment,
  };
}
